import { useCallback } from "react";
import { MessageCircle } from "lucide-react";
import FeedList from "@/components/FeedList";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";

/**
 * Home — 1:1 port of iOS `src/app/(app)/(tabs)/home.tsx`.
 *
 * The Instagram-style top bar (Y wordmark + Search / Bell / Theme icons)
 * now lives in `AppShell`, so this screen is just the merged feed itself.
 *
 * The two feeds `feed/home` and `feed/following` are fetched in parallel
 * each page, deduped by post id, sorted newest-first — so a fresh post
 * from someone you follow surfaces at the top instead of being appended
 * below the home-feed items.
 */
export default function Home() {
  const { user } = useAuth();

  const postTime = (p) => {
    const raw = p?.created_at ?? p?.createdAt ?? p?.timestamp;
    if (typeof raw === "number") return raw;
    const t = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  const fetchHome = useCallback(
    async (page, perPage) => {
      const [hRes, fRes] = await Promise.all([
        api.get(EP.feedHome(page, perPage)).catch(() => null),
        api.get(EP.feedFollowing(page, perPage)).catch(() => null),
      ]);
      const parse = (res) => {
        if (!res) return { items: [], hasMore: false };
        const d = unwrap(res.data);
        const list = Array.isArray(d)
          ? d
          : d?.items || d?.posts || d?.results || d?.data || [];
        return { items: list, hasMore: list.length >= perPage };
      };
      const h = parse(hRes);
      const f = parse(fRes);
      const seen = new Set();
      const items = [];
      for (const it of [...h.items, ...f.items]) {
        const k = it?.id ?? it?.post_id;
        if (k != null && seen.has(k)) continue;
        if (k != null) seen.add(k);
        items.push(it);
      }
      items.sort((a, b) => postTime(b) - postTime(a));
      // Keep paging while EITHER source has more so following-only posts
      // aren't cut off when the home feed runs out first.
      const nextPage = h.hasMore || f.hasMore ? page + 1 : null;
      return { items, nextPage };
    },
    []
  );

  return (
    <div className="w-full" data-testid="home-screen">
      <FeedList
        key={user?.username || "me"}
        fetchPageFn={fetchHome}
        testid="feed-home"
        emptyState={
          <div
            className="px-6 py-16 flex flex-col items-center text-center gap-3"
            data-testid="home-empty"
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: "rgba(118, 56, 250, 0.10)" }}
            >
              <MessageCircle
                size={26}
                strokeWidth={1.8}
                style={{ color: "#7638FA" }}
              />
            </div>
            <h3 className="text-foreground text-[17px] font-bold">
              Your feed is quiet.
            </h3>
            <p className="text-muted-foreground text-[14px] max-w-[280px]">
              Follow people and share posts — everything shows up right here.
            </p>
          </div>
        }
      />
    </div>
  );
}
